import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatZAR, formatDate, MONTHS } from "../api";
import { InvoiceStatusBadge, RepeatBadge } from "../components/StatusBadge";
import { Filter, Save, Trash2, PlusCircle } from "lucide-react";
import { toast } from "sonner";
import BusinessFilter from "../components/BusinessFilter";
import { useBusiness } from "../context/BusinessContext";

export default function ClientMonth() {
  const { business } = useBusiness();
  const [searchParams, setSearchParams] = useSearchParams();
  const [clients, setClients] = useState([]);
  const [cats, setCats] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [editingRef, setEditingRef] = useState({});

  const now = new Date();
  const clientId = searchParams.get("client_id") || "";
  const month = searchParams.get("month") || String(now.getMonth() + 1);
  const year = searchParams.get("year") || String(now.getFullYear());

  useEffect(() => {
    api.get("/clients").then(r => setClients(r.data));
    api.get("/task-categories").then(r => setCats(r.data));
  }, []);

  const load = async () => {
    const params = {};
    if (clientId) params.client_id = clientId;
    else if (business) params.business = business;
    if (month) params.month = Number(month);
    if (year) params.year = Number(year);
    const r = await api.get("/tasks", { params });
    setTasks(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [clientId, month, year, business]);

  const setParam = (k, v) => {
    const np = new URLSearchParams(searchParams);
    if (v) np.set(k, v); else np.delete(k);
    setSearchParams(np);
  };

  const clientName = id => clients.find(c => c.id === id)?.name || "—";
  const catName = id => cats.find(c => c.id === id)?.name || "—";

  const totals = useMemo(() => {
    let invoiced = 0, outstanding = 0;
    tasks.forEach(t => {
      if (t.invoice_ref) invoiced += Number(t.price || 0);
      else outstanding += Number(t.price || 0);
    });
    return { invoiced, outstanding, total: invoiced + outstanding };
  }, [tasks]);
  const clientOptions = useMemo(
    () => clients.filter(c => !business || c.business === business),
    [clients, business]
  );

  const saveRef = async (t) => {
    const ref = editingRef[t.id];
    if (ref === undefined) return;
    try {
      await api.patch(`/tasks/${t.id}`, { invoice_ref: ref });
      toast.success("Saved");
      setEditingRef(s => { const cp = { ...s }; delete cp[t.id]; return cp; });
      load();
    } catch { toast.error("Could not save"); }
  };

  const del = async (t) => {
    if (!confirm("Delete this task?")) return;
    await api.delete(`/tasks/${t.id}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div className="p-6 md:p-10 max-w-7xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="np-label mb-2">Client view</div>
          <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>
            {clientId ? `${clientName(clientId)} - ${MONTHS[Number(month) - 1]} ${year}` : `${business || "All clients"} - ${MONTHS[Number(month) - 1]} ${year}`}
          </h1>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {!clientId && <div className="np-card !p-3"><BusinessFilter /></div>}
          {clientId && (
            <Link
              to={`/enter-work?client_id=${clientId}`}
              data-testid="cm-add-task-btn"
              className="bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-5 py-2.5 inline-flex items-center gap-2 shrink-0"
            >
              <PlusCircle className="w-4 h-4" /> Add task for {clientName(clientId)}
            </Link>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="np-card mb-6">
        <div className="flex items-center gap-2 mb-3">
          <Filter className="w-4 h-4 text-[#0F4F51]" />
          <span className="np-label">Filters</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <select data-testid="cm-client" value={clientId} onChange={e => setParam("client_id", e.target.value)} className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3]">
            <option value="">All clients{business ? ` (${business})` : ""}</option>
            {clientOptions.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select data-testid="cm-month" value={month} onChange={e => setParam("month", e.target.value)} className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3]">
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </select>
          <input
            data-testid="cm-year"
            type="number"
            value={year}
            onChange={e => setParam("year", e.target.value)}
            className="px-3 py-2.5 rounded-lg border border-[#CCCCCC] bg-white outline-none focus:border-[#00ACB3]"
          />
          <button
            data-testid="cm-clear-client"
            onClick={() => { setParam("client_id", ""); }}
            className="rounded-lg border border-[#CCCCCC] text-[#011617] font-semibold px-3 py-2.5 hover:border-[#0F4F51]"
          >Clear client</button>
        </div>
      </div>

      {/* Totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="np-card" data-testid="tot-invoiced">
          <div className="np-label mb-1">Invoiced</div>
          <div className="text-2xl font-extrabold text-[#065F46]" style={{ fontFamily: "Manrope" }}>{formatZAR(totals.invoiced)}</div>
        </div>
        <div className="np-card" data-testid="tot-outstanding">
          <div className="np-label mb-1">Outstanding</div>
          <div className="text-2xl font-extrabold text-[#92400E]" style={{ fontFamily: "Manrope" }}>{formatZAR(totals.outstanding)}</div>
        </div>
        <div className="np-card" data-testid="tot-month">
          <div className="np-label mb-1">Month total</div>
          <div className="text-2xl font-extrabold text-[#0F4F51]" style={{ fontFamily: "Manrope" }}>{formatZAR(totals.total)}</div>
        </div>
      </div>

      <div className="np-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="np-table-header">
              <tr>
                <th className="text-left px-3 py-2.5">Date</th>
                {!clientId && <th className="text-left px-3 py-2.5">Client</th>}
                <th className="text-left px-3 py-2.5">Task</th>
                <th className="text-left px-3 py-2.5">Notes</th>
                <th className="text-left px-3 py-2.5">Repeat</th>
                <th className="text-right px-3 py-2.5">Price</th>
                <th className="text-left px-3 py-2.5">Invoice ref</th>
                <th className="text-left px-3 py-2.5">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {tasks.length === 0 && (
                <tr><td colSpan={9} className="text-center text-[#595959] py-8">No tasks for this period.</td></tr>
              )}
              {tasks.map(t => (
                <tr key={t.id} className="border-b border-[#CCCCCC] hover:bg-[#E5FFFF]/40">
                  <td className="px-3 py-2.5 text-[#011617]">{formatDate(t.date)}</td>
                  {!clientId && <td className="px-3 py-2.5 font-semibold text-[#011617]">{clientName(t.client_id)}</td>}
                  <td className="px-3 py-2.5 text-[#011617]">{catName(t.task_category_id)}</td>
                  <td className="px-3 py-2.5 text-[#595959] max-w-[200px] truncate">{t.notes}</td>
                  <td className="px-3 py-2.5"><RepeatBadge repeat={t.repeat_type} /></td>
                  <td className="px-3 py-2.5 text-right font-bold text-[#011617]">{formatZAR(t.price)}</td>
                  <td className="px-3 py-2.5">
                    <div className="flex items-center gap-1">
                      <input
                        data-testid={`cm-ref-${t.id}`}
                        value={editingRef[t.id] !== undefined ? editingRef[t.id] : (t.invoice_ref || "")}
                        onChange={e => setEditingRef(s => ({ ...s, [t.id]: e.target.value }))}
                        placeholder="ref"
                        className="w-24 px-2 py-1 rounded border border-[#CCCCCC] outline-none focus:border-[#00ACB3]"
                      />
                      {editingRef[t.id] !== undefined && (
                        <button
                          data-testid={`cm-save-ref-${t.id}`}
                          onClick={() => saveRef(t)}
                          className="text-[#00ACB3] hover:text-[#0F4F51]"
                        ><Save className="w-4 h-4" /></button>
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2.5"><InvoiceStatusBadge invoiceRef={t.invoice_ref} /></td>
                  <td className="px-3 py-2.5">
                    <button
                      data-testid={`cm-delete-${t.id}`}
                      onClick={() => del(t)}
                      className="text-[#991B1B] hover:text-[#7F1D1D]"
                    ><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
